'use client';

import { useState, useEffect, useMemo, memo } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import {
    api,
    CategoryData,
    CertificationScore,
} from '@/lib/api';
import { useScoreStatistics } from '@/hooks/useScoreStatistics';
import { ScoreComparison } from './ScoreComparison';
import { CertificationComparisonProvider, useCertificationComparisonContext } from '@/contexts/CertificationComparisonContext';
import { CertificationComparisonSelector, COMPARISON_COLORS } from './CertificationComparisonSelector';
import { useScoreRefresh } from '@/contexts/ScoreRefreshContext';

interface ScorePreviewProps {
    certificationId: string;
}

// Cluster colors for the bar chart
const CLUSTER_COLORS: Record<string, string> = {
    'ENVIRONMENT': '#22c55e', // green
    'DESIGN': '#3b82f6',      // blue
    'ECONOMIC': '#f59e0b',    // amber/orange
    'SOCIAL': '#f97316',      // orange
};

// Default comparison colors (teal-400, violet-400) — overridden per-cert if user sets a custom color
const DEFAULT_COMPARISON_COLORS = ['#2dd4bf', '#a78bfa'];

interface ComparisonBar {
    certId: string;
    name: string;
    score: number | null;
    color: string;
}

// Build a mapping from locale names to EN names by index (same order in both files)
function buildLocaleToEnMap(
    localeData: Record<string, CategoryData[]>,
    enData: Record<string, CategoryData[]>
): Map<string, { enCategory: string; enSubcategory: string }> {
    const map = new Map<string, { enCategory: string; enSubcategory: string }>();

    for (const cluster of ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']) {
        const localeCats = localeData[cluster] || [];
        const enCats = enData[cluster] || [];

        for (let i = 0; i < localeCats.length && i < enCats.length; i++) {
            const localeCat = localeCats[i];
            const enCat = enCats[i];

            for (let j = 0; j < localeCat.subcategories.length && j < enCat.subcategories.length; j++) {
                const localeSub = localeCat.subcategories[j];
                const enSub = enCat.subcategories[j];

                const localeKey = `${cluster}::${localeCat.category}::${localeSub.name}`;
                map.set(localeKey, {
                    enCategory: enCat.category,
                    enSubcategory: enSub.name,
                });
            }
        }
    }

    return map;
}

interface ScoreBarProps {
    score: number | null;
    cluster?: string;
    width?: string;
    comparisons?: ComparisonBar[];
}

const ScoreBar = ({ score, cluster, width = 'w-32', comparisons = [] }: ScoreBarProps) => {
    const percentage = score !== null ? (score / 5) * 100 : 0;
    const color = cluster ? CLUSTER_COLORS[cluster] : '#334155'; // slate-700 for overall

    return (
        <div className={`${width} flex flex-col gap-0.5`}>
            {/* Main bar */}
            <div className="flex items-center h-3.5">
                <div className="w-full h-2 bg-slate-100 overflow-hidden">
                    {score !== null ? (
                        <div
                            className="h-full transition-all duration-300"
                            style={{ width: `${percentage}%`, backgroundColor: color }}
                        />
                    ) : null}
                </div>
            </div>
            {/* Comparison bars */}
            {comparisons.map((comp) => {
                const compPercentage = comp.score !== null ? (comp.score / 5) * 100 : 0;
                return (
                    <div key={comp.certId} className="flex items-center gap-1 group h-3.5">
                        <div className="flex-1 h-1.5 bg-slate-50 overflow-hidden relative">
                            {comp.score !== null ? (
                                <div
                                    className="h-full transition-all duration-300"
                                    style={{ width: `${compPercentage}%`, backgroundColor: comp.color }}
                                />
                            ) : null}
                            {/* Tooltip showing name on hover */}
                            <div className="absolute -top-6 left-0 hidden group-hover:block z-10">
                                <span className="text-[9px] bg-slate-800/80 backdrop-blur-md text-white px-1 py-0.5 border border-white/10 whitespace-nowrap">
                                    {comp.name}: {comp.score?.toFixed(1) ?? '—'}
                                </span>
                            </div>
                        </div>
                        {/* Score number */}
                        <span className="text-[9px] font-medium w-5 text-right tabular-nums text-black">
                            {comp.score?.toFixed(1) ?? '—'}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};



// Inner component that uses the comparison context
const ScorePreviewInner = ({ certificationId }: { certificationId: string }) => {
    const locale = useLocale();
    const t = useTranslations('certification.stage2');
    const { refreshTrigger, weightProfileForClear } = useScoreRefresh();
    const [scores, setScores] = useState<CertificationScore[]>([]);
    const [categoryData, setCategoryData] = useState<Record<string, CategoryData[]>>({});
    const [enCategoryData, setEnCategoryData] = useState<Record<string, CategoryData[]>>({});
    const [loading, setLoading] = useState(true);
    const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set(['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']));
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [allExpanded, setAllExpanded] = useState(true);

    // Comparison context - now provides the live weightProfileId from the parent form
    const {
        visibleComparisons,
        comparisonCerts,
        comparisonScoreMap,
        weightProfileId,
        filterMode,
        setFilterMode,
        hideSelectionControls,
    } = useCertificationComparisonContext();

    // Resolved color per cert (custom or default)
    const comparisonColorMap = useMemo(() =>
        new Map(comparisonCerts.map((c, idx) => [c.id, c.color || DEFAULT_COMPARISON_COLORS[idx % DEFAULT_COMPARISON_COLORS.length]])),
        [comparisonCerts]
    );

    // Score statistics hook - pass weight_profile_id when in 'same_type' mode
    const statsFilterId = filterMode === 'same_type' ? weightProfileId : null;
    const {
        getSubcategoryStats, getCategoryStats, getClusterStats, getOverallStats
    } = useScoreStatistics(statsFilterId);

    // Load static data (run once)
    useEffect(() => {
        const loadStaticData = async () => {
            setLoading(true);
            try {
                const lang = locale === 'pt' ? 'pt' : 'en';
                const [
                    envRes, designRes, econRes, socialRes,
                    envEnRes, designEnRes, econEnRes, socialEnRes
                ] = await Promise.all([
                    fetch(`/api/scores-data/environment?lang=${lang}`),
                    fetch(`/api/scores-data/design?lang=${lang}`),
                    fetch(`/api/scores-data/economic?lang=${lang}`),
                    fetch(`/api/scores-data/social?lang=${lang}`),
                    fetch(`/api/scores-data/environment?lang=en`),
                    fetch(`/api/scores-data/design?lang=en`),
                    fetch(`/api/scores-data/economic?lang=en`),
                    fetch(`/api/scores-data/social?lang=en`),
                ]);

                const envData = await envRes.json();
                const designData = await designRes.json();
                const econData = await econRes.json();
                const socialData = await socialRes.json();

                const envEnData = await envEnRes.json();
                const designEnData = await designEnRes.json();
                const econEnData = await econEnRes.json();
                const socialEnData = await socialEnRes.json();

                setCategoryData({
                    'ENVIRONMENT': envData,
                    'DESIGN': designData,
                    'ECONOMIC': econData,
                    'SOCIAL': socialData,
                });
                setEnCategoryData({
                    'ENVIRONMENT': envEnData,
                    'DESIGN': designEnData,
                    'ECONOMIC': econEnData,
                    'SOCIAL': socialEnData,
                });

                // Expand all categories by default
                const allCatKeys: string[] = [];
                for (const cluster of ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']) {
                    for (const cat of (cluster === 'ENVIRONMENT' ? envData : cluster === 'DESIGN' ? designData : cluster === 'ECONOMIC' ? econData : socialData)) {
                        allCatKeys.push(`${cluster}::${cat.category}`);
                    }
                }
                setExpandedCategories(new Set(allCatKeys));
            } catch (error) {
                console.error('Failed to load score preview data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadStaticData();
    }, [locale]); // Static data dependency

    // Load scores (run on mount and when refresh triggered)
    useEffect(() => {
        const loadScores = async () => {
            try {
                const scoresRes = await api.getCertificationScores(certificationId);
                setScores(scoresRes.scores);
            } catch (error) {
                console.error('Failed to load scores:', error);
            }
        };

        loadScores();
    }, [certificationId, refreshTrigger]);

    // Clear scores selectively when weightProfileForClear changes
    useEffect(() => {
        if (!weightProfileForClear || !weightProfileForClear.weight_tree) return;

        console.log('ScorePreview: Selectively clearing scores based on new weight profile');

        // Build a set of subcategories that have 0% weight in the new profile
        const zeroWeightKeys = new Set<string>();

        weightProfileForClear.weight_tree.forEach((clusterNode: any) => {
            // If cluster has 0 weight, all its children are disabled
            if (clusterNode.weighting === 0 || clusterNode.global_weight === 0) {
                clusterNode.children?.forEach((categoryNode: any) => {
                    categoryNode.children?.forEach((subcategoryNode: any) => {
                        const key = `${clusterNode.name_en}::${categoryNode.name_en}::${subcategoryNode.name_en}`;
                        zeroWeightKeys.add(key);
                    });
                });
            } else {
                // Check category level
                clusterNode.children?.forEach((categoryNode: any) => {
                    if (categoryNode.weighting === 0 || categoryNode.global_weight === 0) {
                        categoryNode.children?.forEach((subcategoryNode: any) => {
                            const key = `${clusterNode.name_en}::${categoryNode.name_en}::${subcategoryNode.name_en}`;
                            zeroWeightKeys.add(key);
                        });
                    } else {
                        // Check subcategory level
                        categoryNode.children?.forEach((subcategoryNode: any) => {
                            if (subcategoryNode.weighting === 0 || subcategoryNode.global_weight === 0) {
                                const key = `${clusterNode.name_en}::${categoryNode.name_en}::${subcategoryNode.name_en}`;
                                zeroWeightKeys.add(key);
                            }
                        });
                    }
                });
            }
        });

        console.log('ScorePreview: Found', zeroWeightKeys.size, 'subcategories with 0% weight');

        // Clear scores for zero-weight subcategories
        setScores(prev => {
            return prev.map(score => {
                const key = `${score.cluster}::${score.category_name}::${score.subcategory_name}`;
                if (zeroWeightKeys.has(key)) {
                    return { ...score, score: null, reliability: 1.0, raw_value: null };
                }
                return score;
            });
        });
    }, [weightProfileForClear]);

    // Toggle all clusters and categories
    const toggleAll = () => {
        if (allExpanded) {
            // Collapse all
            setExpandedClusters(new Set());
            setExpandedCategories(new Set());
        } else {
            // Expand all
            setExpandedClusters(new Set(['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']));
            const allCatKeys: string[] = [];
            for (const cluster of ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']) {
                const categories = categoryData[cluster] || [];
                for (const cat of categories) {
                    allCatKeys.push(`${cluster}::${cat.category}`);
                }
            }
            setExpandedCategories(new Set(allCatKeys));
        }
        setAllExpanded(!allExpanded);
    };

    // Build locale-to-EN mapping
    const localeToEnMap = useMemo(() => {
        return buildLocaleToEnMap(categoryData, enCategoryData);
    }, [categoryData, enCategoryData]);

    // Build score map
    const scoreMap = useMemo(() => {
        const map = new Map<string, CertificationScore>();
        for (const s of scores) {
            const key = `${s.cluster}::${s.category_name}::${s.subcategory_name}`;
            map.set(key, s);
        }
        return map;
    }, [scores]);

    // Calculate aggregated scores for main certification
    const aggregatedScores = useMemo(() => {
        const clusterScores: Record<string, { total: number; count: number }> = {};
        const categoryScores: Record<string, { total: number; count: number }> = {};
        let overallTotal = 0;
        let overallCount = 0;

        for (const cluster of ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']) {
            clusterScores[cluster] = { total: 0, count: 0 };

            const categories = categoryData[cluster] || [];
            for (const cat of categories) {
                const catKey = `${cluster}::${cat.category}`;
                categoryScores[catKey] = { total: 0, count: 0 };

                for (const sub of cat.subcategories) {
                    const localeKey = `${cluster}::${cat.category}::${sub.name}`;
                    const enNames = localeToEnMap.get(localeKey);
                    const dbKey = enNames
                        ? `${cluster}::${enNames.enCategory}::${enNames.enSubcategory}`
                        : localeKey;

                    const scoreData = scoreMap.get(dbKey);
                    if (scoreData?.score != null) {
                        const effectiveScore = scoreData.score * scoreData.reliability;
                        clusterScores[cluster].total += effectiveScore;
                        clusterScores[cluster].count++;
                        categoryScores[catKey].total += effectiveScore;
                        categoryScores[catKey].count++;
                        overallTotal += effectiveScore;
                        overallCount++;
                    }
                }
            }
        }

        const result: Record<string, number | null> = {
            overall: overallCount > 0 ? overallTotal / overallCount : null,
        };

        for (const cluster in clusterScores) {
            const { total, count } = clusterScores[cluster];
            result[cluster] = count > 0 ? total / count : null;
        }

        for (const catKey in categoryScores) {
            const { total, count } = categoryScores[catKey];
            result[catKey] = count > 0 ? total / count : null;
        }

        return result;
    }, [categoryData, scoreMap, localeToEnMap]);

    // Calculate aggregated scores for comparison certifications
    const comparisonAggregatedScores = useMemo(() => {
        const result: Map<string, Record<string, number | null>> = new Map();

        for (const comp of visibleComparisons) {
            const clusterScores: Record<string, { total: number; count: number }> = {};
            const categoryScores: Record<string, { total: number; count: number }> = {};
            let overallTotal = 0;
            let overallCount = 0;

            for (const cluster of ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']) {
                clusterScores[cluster] = { total: 0, count: 0 };

                const categories = categoryData[cluster] || [];
                for (const cat of categories) {
                    const catKey = `${cluster}::${cat.category}`;
                    if (!categoryScores[catKey]) {
                        categoryScores[catKey] = { total: 0, count: 0 };
                    }

                    for (const sub of cat.subcategories) {
                        const localeKey = `${cluster}::${cat.category}::${sub.name}`;
                        const enNames = localeToEnMap.get(localeKey);
                        const dbKey = enNames
                            ? `${cluster}::${enNames.enCategory}::${enNames.enSubcategory}`
                            : localeKey;

                        const scoreData = comp.scores.find(s =>
                            `${s.cluster}::${s.category_name}::${s.subcategory_name}` === dbKey
                        );
                        if (scoreData?.score != null) {
                            const effectiveScore = scoreData.score * scoreData.reliability;
                            clusterScores[cluster].total += effectiveScore;
                            clusterScores[cluster].count++;
                            categoryScores[catKey].total += effectiveScore;
                            categoryScores[catKey].count++;
                            overallTotal += effectiveScore;
                            overallCount++;
                        }
                    }
                }
            }

            const compResult: Record<string, number | null> = {
                overall: overallCount > 0 ? overallTotal / overallCount : null,
            };

            for (const cluster in clusterScores) {
                const { total, count } = clusterScores[cluster];
                compResult[cluster] = count > 0 ? total / count : null;
            }

            for (const catKey in categoryScores) {
                const { total, count } = categoryScores[catKey];
                compResult[catKey] = count > 0 ? total / count : null;
            }

            result.set(comp.id, compResult);
        }

        return result;
    }, [visibleComparisons, categoryData, localeToEnMap]);

    // Build comparison bars for a given key (overall, cluster, category, or subcategory)
    const getComparisonBars = (key: string, cluster?: string): ComparisonBar[] => {
        return visibleComparisons.map((comp) => {
            const compScores = comparisonAggregatedScores.get(comp.id);
            let score: number | null = null;

            if (compScores) {
                // Check if it's a subcategory key (has 3 parts)
                const parts = key.split('::');
                if (parts.length === 3) {
                    // Subcategory - look up directly from scores
                    const scoreData = comp.scores.find(s =>
                        `${s.cluster}::${s.category_name}::${s.subcategory_name}` === key
                    );
                    score = scoreData?.score != null ? scoreData.score * scoreData.reliability : null;
                } else {
                    // Aggregated score
                    score = compScores[key] ?? null;
                }
            }

            return {
                certId: comp.id,
                name: comp.name,
                score,
                color: comparisonColorMap.get(comp.id) || DEFAULT_COMPARISON_COLORS[0],
            };
        });
    };

    const toggleCluster = (cluster: string) => {
        const newExpanded = new Set(expandedClusters);
        if (newExpanded.has(cluster)) {
            newExpanded.delete(cluster);
        } else {
            newExpanded.add(cluster);
        }
        setExpandedClusters(newExpanded);
    };

    const toggleCategory = (clusterWithCategory: string) => {
        const newExpanded = new Set(expandedCategories);
        if (newExpanded.has(clusterWithCategory)) {
            newExpanded.delete(clusterWithCategory);
        } else {
            newExpanded.add(clusterWithCategory);
        }
        setExpandedCategories(newExpanded);
    };

    if (loading) {
        if (hideSelectionControls) return null;
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-black" />
            </div>
        );
    }

    const clusterOrder = ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL'];
    // Removed static clusterLabels map in favor of direct translation lookup

    return (
        <div className="space-y-2 p-6 bg-transparent">
            {/* Comparison Selector and Benchmark Toggle */}
            <div className="mb-4 pb-4 border-b border-slate-200">
                <div className="flex items-center justify-between gap-4 mb-3">
                    <CertificationComparisonSelector />

                    {/* Benchmark Mode Toggle — hidden in read-only compare mode */}
                    {!hideSelectionControls && (
                        <div className="flex items-center gap-2">
                            <div className="flex bg-slate-100 p-0.5 rounded">
                                <button
                                    onClick={() => setFilterMode('all')}
                                    className={`px-3 py-1 text-xs font-medium transition-all duration-200 rounded-sm cursor-pointer ${filterMode === 'all'
                                        ? 'bg-white text-slate-800 shadow-sm'
                                        : 'text-slate-500 hover:text-slate-700'
                                        }`}
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => setFilterMode('same_type')}
                                    disabled={!weightProfileId}
                                    className={`px-3 py-1 text-xs font-medium transition-all duration-200 rounded-sm whitespace-nowrap ${filterMode === 'same_type'
                                        ? 'bg-white text-slate-800 shadow-sm'
                                        : 'text-slate-500 hover:text-slate-700'
                                        } ${!weightProfileId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                    title={!weightProfileId ? 'No typology assigned' : 'Same typology'}
                                >
                                    Same Type
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Overall Score */}
            <div
                className="flex items-center gap-3 py-2 px-2 bg-slate-100 border border-slate-300 cursor-pointer hover:bg-slate-200/50 transition-colors"
                onClick={toggleAll}
                title={allExpanded ? "Collapse All" : "Expand All"}
            >
                <div className="w-64 flex-shrink-0">
                    <span className="text-slate-700 font-semibold text-sm">
                        Overall
                    </span>
                </div>
                <div className="w-40 flex-shrink-0 flex justify-end px-2" onClick={(e) => e.stopPropagation()}>
                    {getOverallStats() && (
                        <ScoreComparison userScore={aggregatedScores.overall} stats={getOverallStats()!} compact />
                    )}
                </div>
                <ScoreBar
                    score={aggregatedScores.overall}
                    width="flex-1"
                    comparisons={getComparisonBars('overall')}
                />
                <div className="text-sm font-mono font-semibold text-black w-8 text-right">
                    {aggregatedScores.overall?.toFixed(1) ?? '—'}
                </div>
            </div>

            {/* Clusters */}
            {clusterOrder.map((cluster) => {
                const isExpanded = expandedClusters.has(cluster);
                const categories = categoryData[cluster] || [];

                return (
                    <div key={cluster}>
                        {/* Cluster Row */}
                        <div
                            className="flex items-center gap-3 py-1.5 px-2 hover:bg-slate-50 cursor-pointer border border-transparent"
                            onClick={() => toggleCluster(cluster)}
                        >
                            <div className="w-64 flex-shrink-0 flex items-center gap-2 pl-4">
                                <svg
                                    className={`w-3 h-3 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="font-medium text-xs text-black">
                                    {t(cluster.toLowerCase() as any)}
                                </span>
                            </div>
                            <div className="w-40 flex-shrink-0 flex justify-end px-2">
                                {getClusterStats(cluster) && (
                                    <ScoreComparison userScore={aggregatedScores[cluster]} stats={getClusterStats(cluster)!} compact />
                                )}
                            </div>
                            <ScoreBar
                                score={aggregatedScores[cluster]}
                                cluster={cluster}
                                width="flex-1"
                                comparisons={getComparisonBars(cluster, cluster)}
                            />
                            <div className="text-xs font-mono text-black w-8 text-right">
                                {aggregatedScores[cluster]?.toFixed(1) ?? '—'}
                            </div>
                        </div>

                        {/* Categories */}
                        {
                            isExpanded && categories.map((cat) => {
                                const catKey = `${cluster}::${cat.category}`;
                                const isCatExpanded = expandedCategories.has(catKey);

                                return (
                                    <div key={catKey}>
                                        {/* Category Row */}
                                        <div
                                            className="flex items-center gap-3 py-1 px-2 hover:bg-slate-50 cursor-pointer border border-transparent"
                                            onClick={() => toggleCategory(catKey)}
                                        >
                                            <div className="w-64 flex-shrink-0 flex items-center gap-2 pl-10">
                                                <svg
                                                    className={`w-3 h-3 text-slate-400 transition-transform ${isCatExpanded ? 'rotate-90' : ''}`}
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                                <span className="text-xs text-black truncate" title={cat.category}>
                                                    {cat.category.length > 30 ? cat.category.substring(0, 30) + '...' : cat.category}
                                                </span>
                                            </div>
                                            <div className="w-40 flex-shrink-0 flex justify-end px-2">
                                                {(() => {
                                                    // Resolve the correct English key for stats lookup
                                                    // For categories, we need to find ANY subcategory mapping to get the category EN name, 
                                                    // or just rely on the fact that we might have a map for the category itself if we built it that way.
                                                    // But localeToEnMap keys are cluster::cat::sub.
                                                    // Let's find the first mapping that matches this category.
                                                    const prefix = `${cluster}::${cat.category}::`;
                                                    let enCategory = cat.category;

                                                    for (const [key, value] of localeToEnMap.entries()) {
                                                        if (key.startsWith(prefix)) {
                                                            enCategory = value.enCategory;
                                                            break;
                                                        }
                                                    }

                                                    const stats = getCategoryStats(cluster, enCategory);
                                                    return stats && (
                                                        <ScoreComparison userScore={aggregatedScores[catKey]} stats={stats} compact />
                                                    );
                                                })()}
                                            </div>
                                            <ScoreBar
                                                score={aggregatedScores[catKey]}
                                                cluster={cluster}
                                                width="flex-1"
                                                comparisons={getComparisonBars(catKey, cluster)}
                                            />
                                            <div className="text-xs font-mono text-black w-8 text-right">
                                                {aggregatedScores[catKey]?.toFixed(1) ?? '—'}
                                            </div>
                                        </div>

                                        {/* Subcategories */}
                                        {isCatExpanded && cat.subcategories.map((sub) => {
                                            const localeKey = `${cluster}::${cat.category}::${sub.name}`;
                                            const enNames = localeToEnMap.get(localeKey);
                                            const dbKey = enNames
                                                ? `${cluster}::${enNames.enCategory}::${enNames.enSubcategory}`
                                                : localeKey;

                                            const scoreData = scoreMap.get(dbKey);
                                            const effectiveScore = scoreData?.score != null
                                                ? scoreData.score * scoreData.reliability
                                                : null;

                                            return (
                                                <div key={sub.name} className="flex items-center gap-3 py-0.5 px-2 border border-transparent">
                                                    <div className="w-64 flex-shrink-0 flex items-center gap-2 pl-20">
                                                        <span className="text-xs text-black truncate" title={sub.name}>
                                                            {sub.name.length > 35 ? sub.name.substring(0, 35) + '...' : sub.name}
                                                        </span>
                                                    </div>
                                                    <div className="w-40 flex-shrink-0 flex justify-end px-2">
                                                        {(() => {
                                                            // Resolve the correct English key for stats lookup
                                                            const localeKey = `${cluster}::${cat.category}::${sub.name}`;
                                                            const enNames = localeToEnMap.get(localeKey);
                                                            const stats = enNames
                                                                ? getSubcategoryStats(cluster, enNames.enCategory, enNames.enSubcategory)
                                                                : getSubcategoryStats(cluster, cat.category, sub.name);

                                                            return stats && (
                                                                <ScoreComparison userScore={effectiveScore} stats={stats} compact />
                                                            );
                                                        })()}
                                                    </div>
                                                    <ScoreBar
                                                        score={effectiveScore}
                                                        cluster={cluster}
                                                        width="flex-1"
                                                        comparisons={getComparisonBars(dbKey, cluster)}
                                                    />
                                                    <div className="text-xs font-mono text-black w-8 text-right">
                                                        {effectiveScore?.toFixed(1) ?? '—'}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })
                        }
                    </div>
                );
            })}
        </div >
    );
};

// Component that uses the comparison context (expects context from parent)
const ScorePreviewComponent = ({ certificationId }: { certificationId: string }) => {
    return <ScorePreviewInner certificationId={certificationId} />;
};

export const ScorePreview = memo(ScorePreviewComponent);
